import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import AuthUser from "../../Hooks/authUser";

const Pricing = () => {
  const issueDate = moment().format("YYYY-MM-DD");
  const { userInfo } = AuthUser();
  const navigate = useNavigate();
  const [sub, setSub] = useState([]);
  const [plans, setPlans] = useState([]);
  const isExpire = sub?.expireDate
    ? moment(issueDate).isAfter(sub?.expireDate)
    : true;

  useEffect(() => {
    if (userInfo?._id) {
      fetch(
        `https://servercareseebd.careseebd.com/api/v1/package/specific?fieldName=userId&&fieldValue=${userInfo._id}`
      )
        .then((res) => res.json())
        .then((data) => {
          if (data?.data?.length) {
            setSub(data?.data?.[0]);
          }
        });
    }
  }, [userInfo?._id]);

  useEffect(() => {
    fetch(`https://servercareseebd.careseebd.com/api/v1/plan/getPlan`)
      .then((res) => res.json())
      .then((data) => {
        setPlans(data?.data || []);
      });
  }, []);

  const handlePayment = (packagePrice, packageName, day) => {
    const today = moment();
    const issueDate = moment().format("YYYY-MM-DD");
    const expireDate = today.add(day ? day : 1, "days").format("YYYY-MM-DD");
    if (!userInfo) {
      navigate("/login");
      return toast.warning("You must be logged in to purchase packages!");
    } else if (!isExpire) {
      return toast.warning(
        `You already have an active package. Expire Date is ${sub?.expireDate}`
      );
    }
    const data = {
      totalDay: day,
      userEmail: userInfo?.email,
      userId: userInfo?._id,
      userName: userInfo?.name,
      issueDate,
      expireDate,
      packagePrice,
      packageName,
    };
    fetch("https://servercareseebd.careseebd.com/api/v1/package/addPackage", {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.url) {
          window.location.replace(data?.url);
        } else {
          toast.error(data?.message || "Something went wrong. Please try again.");
        }
      });
  };

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      {/* Background gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(99,102,241,0.06),transparent)] pointer-events-none" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-indigo-50/50 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-sky-50/50 blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-widest mb-4">
            <Icon icon="solar:wallet-money-bold" className="w-3.5 h-3.5" />
            Pricing Plans
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight mb-5">
            Flexible Plans for <span className="text-gradient">Every Patient</span>
          </h1>
          <p className="text-base text-gray-500 max-w-xl mx-auto">
            Choose the right subscription plan to access CareSee's vision therapy sessions, progress trackers, and expert doctor reviews.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 justify-center items-stretch">
          {plans?.map((plan, i) => {
            // Determine if the card is highlighted/popular (just highlight one for premium contrast)
            const isPopular = i === 1 || plan?.title?.toLowerCase().includes("popular") || plan?.title?.toLowerCase().includes("standard");

            return (
              <div
                key={plan?._id}
                className={`relative flex flex-col justify-between rounded-2xl p-8 transition-all duration-300 ${isPopular
                  ? "bg-slate-900 text-white border-2 border-indigo-500 shadow-xl shadow-indigo-950/10 -translate-y-2"
                  : "bg-white text-gray-900 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1"
                  }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <span className="absolute top-0 right-6 transform -translate-y-1/2 bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm">
                    Popular
                  </span>
                )}

                <div>
                  {/* Package Type Title */}
                  <h3 className={`text-xs font-bold uppercase tracking-widest mb-4 ${isPopular ? "text-indigo-400" : "text-indigo-600"
                    }`}>
                    {plan?.title}
                  </h3>

                  {/* Price */}
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-extrabold font-heading tracking-tight">
                      ৳{plan?.price}
                    </span>
                    <span className={`text-xs ml-2 font-medium ${isPopular ? "text-gray-400" : "text-gray-500"
                      }`}>
                      / {plan?.day ? `${plan.day} Days` : "Plan"}
                    </span>
                  </div>

                  {/* Divider */}
                  <div className={`w-full h-px my-6 ${isPopular ? "bg-white/10" : "bg-gray-100"
                    }`} />

                  {/* Facilities List */}
                  <ul className="space-y-4 mb-8">
                    {plan?.facilities?.map((facility, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Icon
                          icon="solar:check-circle-bold-duotone"
                          className={`w-5 h-5 flex-shrink-0 ${isPopular ? "text-indigo-400" : "text-indigo-500"
                            }`}
                        />
                        <span className={`text-sm leading-relaxed ${isPopular ? "text-gray-300" : "text-gray-600"
                          }`}>
                          {facility}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Purchase Button */}
                <button
                  onClick={() =>
                    handlePayment(plan?.price, plan?.title, plan?.day)
                  }
                  className={`w-full py-3.5 px-4 rounded-xl text-xs font-bold tracking-wider uppercase transition-all duration-200 cursor-pointer ${isPopular
                    ? "bg-indigo-500 hover:bg-indigo-600 text-white shadow-lg shadow-indigo-500/20"
                    : "bg-gray-50 hover:bg-gray-100 text-gray-800 border border-gray-200/80"
                    }`}
                >
                  Choose Plan
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
